/**
 * Location Privacy
 *
 * Reduces GPS precision before sharing to protect user privacy.
 */

import type { GeoLocation, FuzzedLocation } from '@/lib/types'
import { encodeGeohash } from './geohash'

// Target precision: ~100m
// This is achieved by rounding to 3 decimal places (~110m at equator)
const PRECISION_DECIMAL_PLACES = 3
const FUZZ_GEOHASH_PRECISION = 7 // ~153m

/**
 * Fuzz a location to reduce precision to ~100m
 */
export function fuzzLocation(location: GeoLocation): FuzzedLocation {
  const multiplier = Math.pow(10, PRECISION_DECIMAL_PLACES)

  const fuzzedLat = Math.round(location.latitude * multiplier) / multiplier
  const fuzzedLng = Math.round(location.longitude * multiplier) / multiplier

  // Add small random offset for additional privacy
  const latOffset = (Math.random() - 0.5) * 0.0005 // ~50m
  const lngOffset = (Math.random() - 0.5) * 0.0005

  const finalLat = fuzzedLat + latOffset
  const finalLng = fuzzedLng + lngOffset

  return {
    latitude: finalLat,
    longitude: finalLng,
    geohash: encodeGeohash({ latitude: finalLat, longitude: finalLng }, FUZZ_GEOHASH_PRECISION),
  }
}

/**
 * Round a location without random offset
 * Used for consistent POI display
 */
export function roundLocation(location: GeoLocation): GeoLocation {
  const multiplier = Math.pow(10, PRECISION_DECIMAL_PLACES)

  return {
    latitude: Math.round(location.latitude * multiplier) / multiplier,
    longitude: Math.round(location.longitude * multiplier) / multiplier,
    accuracy: location.accuracy,
    timestamp: location.timestamp,
  }
}

/**
 * Check if location is precise enough for geofencing
 * Returns false if GPS accuracy is too poor
 */
export function isLocationPreciseEnough(
  location: GeoLocation,
  requiredAccuracy: number = 50
): boolean {
  if (!location.accuracy) {
    return true // If no accuracy data, assume it's fine
  }
  return location.accuracy <= requiredAccuracy
}

/**
 * Get a privacy-safe description of a location
 * Returns intersection/landmark rather than exact coordinates
 */
export function getPrivacyFriendlyDescription(
  _location: GeoLocation
): string {
  // In a real implementation, this would use a reverse geocoding service
  // to get nearest intersection or landmark
  return 'Near current location'
}
